import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, getErrorMessage } from "@/lib/queryClient";
import { useLocation } from "wouter";
import DateInput from "@/components/date-input";
import { ScheduleTimePicker } from "@/components/schedule-time-picker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  CalendarClock,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  MoreHorizontal,
  BookOpen,
  Ban,
  Trash2,
  UserRound,
  Plus,
} from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  subDays,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
} from "date-fns";
import type { Lesson } from "@contracts/lesson";
import type { Student } from "@contracts/student";
import type { CalendarEvent as OneTimeCalendarEvent } from "@contracts/calendar-event";
import type { ScheduleRule, ScheduleException } from "@contracts/schedule";
import type { TeacherProfile } from "@contracts/teacher";
import {
  localDateTimeToUTC,
  getLocalDateParts,
  formatWallTimeHHmmInTimezone,
  calendarDateKeyInTimezone,
  addWallCalendarDays,
  getMondayOfWeekWallDateInTimezone,
} from "@/lib/timezone-schedule";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ScheduleEventDialog from "@/components/schedule-event-dialog";
import UndoDeleteBanner from "@/components/undo-delete-banner";

type ViewMode = "month" | "week" | "day";

interface CalendarEvent {
  id: string;
  studentId: string;
  scheduledDate: Date;
  originalDate?: Date;
  startTime: string;
  duration: number;
  lessonType: string;
  address: string | null;
  paymentStatus: string;
  unitRate: string;
  unitCurrency: string;
  isVirtual: boolean;
  lessonId?: string;
  source: "lesson" | "virtual" | "custom";
  title?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PX_MIN = 28;
const PX_MAX = 120;

function getEventEndUtc(event: CalendarEvent): Date {
  return new Date(event.scheduledDate.getTime() + event.duration * 60 * 1000);
}

function isEventPassed(event: CalendarEvent): boolean {
  return getEventEndUtc(event).getTime() < Date.now();
}

function getEventChromeClasses(event: CalendarEvent): string {
  const online = event.lessonType === "online";
  const passed = isEventPassed(event);
  const ongoing = !event.isVirtual && isOngoing(event);

  if (event.isVirtual) {
    if (passed) {
      return online
        ? "border-2 border-blue-500/70 bg-blue-500/25 text-foreground"
        : "border-2 border-amber-600/70 bg-amber-500/25 text-foreground";
    }
    return online
      ? "border-2 border-dashed border-blue-400/50 bg-blue-500/10 text-blue-900 dark:text-blue-100"
      : "border-2 border-dashed border-amber-500/50 bg-amber-500/10 text-amber-900 dark:text-amber-100";
  }

  if (ongoing) {
    return "border-2 border-amber-500 bg-amber-100/80 dark:bg-amber-950/50 text-foreground";
  }
  if (passed) {
    return online
      ? "border-2 border-blue-600 bg-blue-200/50 dark:bg-blue-950/60 text-foreground"
      : "border-2 border-amber-700 bg-amber-200/50 dark:bg-amber-950/50 text-foreground";
  }
  return online
    ? "border-2 border-dashed border-blue-400/55 bg-blue-500/12 text-blue-950 dark:text-blue-100"
    : "border-2 border-dashed border-amber-500/55 bg-amber-500/12 text-amber-950 dark:text-amber-100";
}

function isOngoing(event: CalendarEvent): boolean {
  const now = Date.now();
  const start = event.scheduledDate.getTime();
  const end = start + event.duration * 60 * 1000;
  return now >= start && now < end;
}

interface LayoutInfo {
  col: number;
  totalCols: number;
}

function computeOverlapLayout(events: CalendarEvent[], tz: string): Map<string, LayoutInfo> {
  const result = new Map<string, LayoutInfo>();
  if (events.length === 0) return result;

    const sorted = [...events].sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime());

  const items = sorted.map(e => {
    const { hour: h, minute: m } = getLocalDateParts(e.scheduledDate, tz);
    return {
      id: e.id,
      start: h * 60 + m,
      end: h * 60 + m + e.duration,
    };
  });

  const groups: typeof items[] = [];
  let currentGroup: typeof items = [];
  let groupEnd = 0;

  for (const ev of items) {
    if (currentGroup.length === 0 || ev.start < groupEnd) {
      currentGroup.push(ev);
      groupEnd = Math.max(groupEnd, ev.end);
    } else {
      groups.push(currentGroup);
      currentGroup = [ev];
      groupEnd = ev.end;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  for (const group of groups) {
    const columns: typeof items[] = [];
    for (const ev of group) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        const lastInCol = columns[c][columns[c].length - 1];
        if (lastInCol.end <= ev.start) {
          columns[c].push(ev);
          result.set(ev.id, { col: c, totalCols: 0 });
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([ev]);
        result.set(ev.id, { col: columns.length - 1, totalCols: 0 });
      }
    }
    for (const ev of group) {
      const info = result.get(ev.id)!;
      info.totalCols = columns.length;
    }
  }

  return result;
}

function generateVirtualEvents(
  students: Student[],
  existingLessons: Lesson[],
  rangeStart: Date,
  rangeEnd: Date,
  timezone: string
): CalendarEvent[] {
  const existingKeys = new Set<string>();
  for (const l of existingLessons) {
    existingKeys.add(`${l.studentId}_${new Date(l.scheduledDate).getTime()}`);
    if ((l as Lesson & { originalScheduledDate?: string }).originalScheduledDate) {
      existingKeys.add(
        `${l.studentId}_${new Date((l as Lesson & { originalScheduledDate?: string }).originalScheduledDate!).getTime()}`
      );
    }
  }

  const virtuals: CalendarEvent[] = [];
  let idCounter = 0;

  for (const student of students) {
    const rules = (student.scheduleRules as ScheduleRule[]) || [];
    if (rules.length === 0) continue;

    const exceptions = (student.scheduleExceptions as ScheduleException[]) || [];
    const exceptionMap = new Map<string, ScheduleException>();
    for (const exc of exceptions) {
      exceptionMap.set(exc.originalDate, exc);
    }

    const scheduleStart = student.scheduleStartDate ? new Date(student.scheduleStartDate) : null;
    const monStart = getMondayOfWeekWallDateInTimezone(rangeStart, timezone);
    const totalDays = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 14;
    const totalWeeks = Math.ceil(totalDays / 7) + 2;

    for (let w = 0; w < totalWeeks; w++) {
      for (const rule of rules) {
        const offsetFromMonday = rule.dayOfWeek === 0 ? 6 : rule.dayOfWeek - 1;
        const wall = addWallCalendarDays(monStart.year, monStart.month, monStart.day, offsetFromMonday + w * 7, timezone);

        const [h, m] = rule.startTime.split(":").map(Number);
        const eventDate = localDateTimeToUTC(wall.year, wall.month, wall.day, h, m, timezone);

        if (scheduleStart && eventDate < scheduleStart) continue;

        if (rule.recurrenceType === "biweekly" && scheduleStart) {
          const weeksDiff = Math.round(
            (eventDate.getTime() - scheduleStart.getTime()) / (7 * 24 * 60 * 60 * 1000)
          );
          if (weeksDiff % 2 !== 0) continue;
        }

        if (eventDate < rangeStart || eventDate > rangeEnd) continue;

        const key = `${student.id}_${eventDate.getTime()}`;
        if (existingKeys.has(key)) continue;

        const exception = exceptionMap.get(eventDate.toISOString());
        if (exception?.cancelled) continue;

        let finalDate = eventDate;
        let finalDuration = rule.duration || student.lessonDuration;

        if (exception) {
          if (exception.newDate) finalDate = new Date(exception.newDate);
          if (exception.newDuration) finalDuration = exception.newDuration;
        }

        if (finalDate < rangeStart || finalDate > rangeEnd) continue;

        const finalStartTime = exception?.newDate
          ? formatWallTimeHHmmInTimezone(new Date(exception.newDate), timezone)
          : rule.startTime;

        virtuals.push({
          id: `virtual_${student.id}_${idCounter++}`,
          studentId: student.id,
          scheduledDate: finalDate,
          originalDate: eventDate,
          startTime: finalStartTime,
          duration: finalDuration,
          lessonType: student.lessonType,
          address: student.address,
          paymentStatus: "scheduled",
          unitRate: student.unitRate,
          unitCurrency: student.unitCurrency,
          isVirtual: true,
          source: "virtual",
          title: student.name,
        });
      }
    }
  }

  return virtuals;
}

export default function CalendarPage() {
  const [, navigate] = useLocation();
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [editingVirtualEvent, setEditingVirtualEvent] = useState<CalendarEvent | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editDuration, setEditDuration] = useState(60);
  const [pxPerHour, setPxPerHour] = useState(60);
  const [removeCalOnDelete, setRemoveCalOnDelete] = useState(true);
  const [lessonPendingDelete, setLessonPendingDelete] = useState<Lesson | null>(null);
  const [showScheduleEvent, setShowScheduleEvent] = useState(false);
  const [nowTick, setNowTick] = useState(0);
  const { toast } = useToast();
  const timelineRef = useRef<HTMLDivElement>(null);
  const pinchRef = useRef<{ distance: number; pxPerHour: number } | null>(null);
  const pendingLessonDeleteRef = useRef<{ timer: ReturnType<typeof setTimeout> } | null>(null);
  const pendingEventDeleteRef = useRef<{ timer: ReturnType<typeof setTimeout> } | null>(null);
  const [pendingDeleteUi, setPendingDeleteUi] = useState<{
    message: string;
    deadline: number;
    durationMs: number;
  } | null>(null);

  const getMinuteOffsetFromTime = (timeStr: string) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * pxPerHour + (m / 60) * pxPerHour;
  };

  const getMinuteOffsetFromScheduled = (scheduledDate: Date) => {
    const { hour: h, minute: m } = getLocalDateParts(scheduledDate, teacherTz);
    return h * pxPerHour + (m / 60) * pxPerHour;
  };

  const getLessonHeightPx = (duration: number) =>
    Math.max((duration / 60) * pxPerHour, 24);

  const handleTimelineWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -6 : 6;
    setPxPerHour((p) => Math.min(PX_MAX, Math.max(PX_MIN, p + delta)));
  };

  const handleTouchPinchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) {
      pinchRef.current = null;
      return;
    }
    const [t1, t2] = Array.from(e.touches);
    const distance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    pinchRef.current = { distance, pxPerHour };
  };

  const handleTouchPinchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2 || !pinchRef.current) return;
    const [t1, t2] = Array.from(e.touches);
    const distance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    if (!Number.isFinite(distance) || pinchRef.current.distance === 0) return;
    e.preventDefault();
    e.stopPropagation();
    const scale = distance / pinchRef.current.distance;
    const nextPx = Math.round(pinchRef.current.pxPerHour * scale);
    setPxPerHour(Math.min(PX_MAX, Math.max(PX_MIN, nextPx)));
  };

  const handleTouchPinchEnd = () => {
    pinchRef.current = null;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTick(t => t + 1);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (pendingLessonDeleteRef.current) clearTimeout(pendingLessonDeleteRef.current.timer);
      if (pendingEventDeleteRef.current) clearTimeout(pendingEventDeleteRef.current.timer);
      setPendingDeleteUi(null);
    };
  }, []);

  const { data: lessons = [], isLoading: lessonsLoading } = useQuery<Lesson[]>({
    queryKey: ["/api/lessons"],
  });
  const { data: customEvents = [] } = useQuery<OneTimeCalendarEvent[]>({
    queryKey: ["/api/calendar/events"],
  });

  const { data: students = [] } = useQuery<Student[]>({
    queryKey: ["/api/students"],
  });

  const { data: profile } = useQuery<TeacherProfile>({
    queryKey: ["/api/profile"],
  });

  const teacherTz =
    profile?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "UTC";

  const todayKeyInTeacherTz = useMemo(
    () => calendarDateKeyInTimezone(new Date(), teacherTz),
    [teacherTz, nowTick]
  );

  const weekColumnInfos = useMemo(() => {
    const mon = getMondayOfWeekWallDateInTimezone(currentDate, teacherTz);
    return Array.from({ length: 7 }, (_, i) => {
      const wall = addWallCalendarDays(mon.year, mon.month, mon.day, i, teacherTz);
      const key = `${wall.year}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}`;
      const noon = localDateTimeToUTC(wall.year, wall.month, wall.day, 12, 0, teacherTz);
      return { wall, key, noon };
    });
  }, [currentDate, teacherTz]);

  const monthGridCells = useMemo(() => {
    const ref = getLocalDateParts(currentDate, teacherTz);
    const y = ref.year;
    const m = ref.month;
    const firstNoon = localDateTimeToUTC(y, m, 1, 12, 0, teacherTz);
    const mon = getMondayOfWeekWallDateInTimezone(firstNoon, teacherTz);
    return Array.from({ length: 42 }, (_, i) => {
      const wall = addWallCalendarDays(mon.year, mon.month, mon.day, i, teacherTz);
      const inMonth = wall.month === m && wall.year === y;
      const key = `${wall.year}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}`;
      const noon = localDateTimeToUTC(wall.year, wall.month, wall.day, 12, 0, teacherTz);
      return { wall, key, noon, inMonth };
    });
  }, [currentDate, teacherTz]);

  const syncFromGoogleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/calendar/sync-from-google");
      return res.json();
    },
    onSuccess: (data: { synced: number }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      if (data.synced > 0) {
        toast({
          title: `Synced ${data.synced} change${data.synced > 1 ? "s" : ""} from Google Calendar`,
          variant: "success",
        });
      } else {
        toast({
          title: "Calendar is up to date",
          description: "No new changes from Google Calendar.",
          variant: "success",
        });
      }
    },
    onError: (err: unknown) => {
      toast({
        title: getErrorMessage(err, "Could not sync from Google Calendar"),
        variant: "destructive",
      });
    },
  });

  const studentMap = useMemo(() => {
    const map: Record<string, Student> = {};
    students.forEach(s => { map[s.id] = s; });
    return map;
  }, [students]);

  const calendarRange = useMemo(() => {
    if (viewMode === "month") {
      const ms = startOfMonth(currentDate);
      const me = endOfMonth(ms);
      return {
        start: startOfWeek(ms, { weekStartsOn: 1 }),
        end: endOfWeek(me, { weekStartsOn: 1 }),
      };
    }
    if (viewMode === "week") {
      const mon = getMondayOfWeekWallDateInTimezone(currentDate, teacherTz);
      const sun = addWallCalendarDays(mon.year, mon.month, mon.day, 6, teacherTz);
      return {
        start: localDateTimeToUTC(mon.year, mon.month, mon.day, 0, 0, teacherTz),
        end: localDateTimeToUTC(sun.year, sun.month, sun.day, 23, 59, teacherTz),
      };
    }
    const dayWall = getLocalDateParts(currentDate, teacherTz);
    return {
      start: localDateTimeToUTC(dayWall.year, dayWall.month, dayWall.day, 0, 0, teacherTz),
      end: localDateTimeToUTC(dayWall.year, dayWall.month, dayWall.day, 23, 59, teacherTz),
    };
  }, [viewMode, currentDate, teacherTz]);

  const allEvents = useMemo<CalendarEvent[]>(() => {
    const realEvents: CalendarEvent[] = lessons.map(l => ({
      id: l.id,
      studentId: l.studentId,
      scheduledDate: new Date(l.scheduledDate),
      originalDate: l.originalScheduledDate ? new Date(l.originalScheduledDate) : undefined,
      startTime: formatWallTimeHHmmInTimezone(new Date(l.scheduledDate), teacherTz),
      duration: l.duration,
      lessonType: l.lessonType,
      address: l.address,
      paymentStatus: l.paymentStatus,
      unitRate: l.unitRate,
      unitCurrency: l.unitCurrency,
      isVirtual: false,
      lessonId: l.id,
      source: "lesson",
      title: studentMap[l.studentId]?.name,
    }));

    const virtuals = generateVirtualEvents(
      students,
      lessons,
      calendarRange.start,
      calendarRange.end,
      teacherTz
    );

    const oneTimes: CalendarEvent[] = customEvents.map((event) => {
      const linkedStudent = event.studentId ? studentMap[event.studentId] : undefined;
      return {
        id: event.id,
        studentId: event.studentId || "",
        scheduledDate: new Date(event.scheduledDate),
        startTime: formatWallTimeHHmmInTimezone(new Date(event.scheduledDate), teacherTz),
        duration: event.duration,
        lessonType: event.lessonType,
        address: event.address,
        paymentStatus: "scheduled",
        unitRate: linkedStudent?.unitRate || "0",
        unitCurrency: linkedStudent?.unitCurrency || "",
        isVirtual: false,
        source: "custom",
        title: event.title,
      };
    });

    return [...realEvents, ...virtuals, ...oneTimes];
  }, [lessons, students, customEvents, calendarRange, teacherTz, studentMap]);

  useEffect(() => {
    if ((viewMode === "week" || viewMode === "day") && timelineRef.current) {
      timelineRef.current.scrollTop = 7 * pxPerHour;
    }
  }, [viewMode, currentDate, pxPerHour]);

  const createFromScheduleMutation = useMutation({
    mutationFn: async (event: CalendarEvent) => {
      const originalOccurrenceStart = (event.originalDate || event.scheduledDate).toISOString();
      const res = await apiRequest("POST", "/api/lessons/from-schedule", {
        studentId: event.studentId,
        originalOccurrenceStart,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      toast({ title: "Lesson created from schedule", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not create lesson"), variant: "destructive" });
    },
  });

  const createFromCustomEventMutation = useMutation({
    mutationFn: async (event: CalendarEvent) => {
      const res = await apiRequest("POST", `/api/calendar/events/${event.id}/create-lesson`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/events"] });
      toast({ title: "Lesson created from one-time event", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not create lesson"), variant: "destructive" });
    },
  });

  const cancelOccurrenceMutation = useMutation({
    mutationFn: async (event: CalendarEvent) => {
      const originalDate = (event.originalDate || event.scheduledDate).toISOString();
      const res = await apiRequest("POST", `/api/students/${event.studentId}/reschedule`, {
        originalDate,
        cancelled: true,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      toast({ title: "Occurrence cancelled", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to cancel", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest("PUT", `/api/lessons/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      setEditingLesson(null);
      toast({ title: "Lesson updated", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not update lesson"), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      id,
      removeCalendarEvent: rm,
    }: {
      id: string;
      removeCalendarEvent: boolean;
    }) => {
      const q = rm ? "" : "?removeCalendarEvent=false";
      await apiRequest("DELETE", `/api/lessons/${id}${q}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      setEditingLesson(null);
      setLessonPendingDelete(null);
      toast({ title: "Lesson deleted", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not delete lesson"), variant: "destructive" });
    },
  });

  const deleteCustomEventMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/calendar/events/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/events"] });
      toast({ title: "Event deleted", variant: "success" });
    },
    onError: (err: unknown) => {
      toast({ title: getErrorMessage(err, "Could not delete event"), variant: "destructive" });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ lessonId, status }: { lessonId: string; status: string }) => {
      const res = await apiRequest("PATCH", `/api/lessons/${lessonId}/status`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: async ({ event, date, time, duration }: { event: CalendarEvent; date: string; time: string; duration: number }) => {
      const [yy, mo, dd] = date.split("-").map(Number);
      const [th, tm] = time.split(":").map(Number);
      const newDate = localDateTimeToUTC(yy, mo, dd, th, tm, teacherTz);
      if (event.source === "custom") {
        const res = await apiRequest("PUT", `/api/calendar/events/${event.id}`, {
          scheduledDate: newDate.toISOString(),
          duration,
        });
        return res.json();
      }
      const originalDate = (event.originalDate || event.scheduledDate).toISOString();
      const res = await apiRequest("POST", `/api/students/${event.studentId}/reschedule`, {
        originalDate,
        newDate: newDate.toISOString(),
        newDuration: duration,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/events"] });
      setEditingVirtualEvent(null);
      toast({ title: "Event rescheduled", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to reschedule", variant: "destructive" });
    },
  });

  function getEventsForWallDay(dateKey: string) {
    return allEvents
      .filter(e => calendarDateKeyInTimezone(e.scheduledDate, teacherTz) === dateKey)
      .sort((a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime());
  }

  function getEventDisplayName(event: CalendarEvent): string {
    if (event.source === "custom") return event.title || "Event";
    return studentMap[event.studentId]?.name || "?";
  }

  function openLessonRescheduleDialog(lesson: Lesson) {
    const d = new Date(lesson.scheduledDate);
    const wall = getLocalDateParts(d, teacherTz);
    setEditDate(`${wall.year}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}`);
    setEditTime(formatWallTimeHHmmInTimezone(d, teacherTz));
    setEditDuration(lesson.duration);
    setEditingLesson(lesson);
    setEditingVirtualEvent(null);
  }

  function openLessonDeleteConfirm(lesson: Lesson) {
    setEditingLesson(null);
    setEditingVirtualEvent(null);
    setLessonPendingDelete(lesson);
    setRemoveCalOnDelete(true);
  }

  function openVirtualEditDialog(event: CalendarEvent) {
    const d = event.scheduledDate;
    const wall = getLocalDateParts(d, teacherTz);
    setEditDate(`${wall.year}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}`);
    setEditTime(formatWallTimeHHmmInTimezone(d, teacherTz));
    setEditDuration(event.duration);
    setEditingVirtualEvent(event);
    setEditingLesson(null);
  }

  function cancelPendingLessonDelete() {
    if (!pendingLessonDeleteRef.current) return;
    clearTimeout(pendingLessonDeleteRef.current.timer);
    pendingLessonDeleteRef.current = null;
    setPendingDeleteUi(null);
  }

  function queueLessonDelete(payload: { id: string; removeCalendarEvent: boolean }) {
    if (pendingLessonDeleteRef.current) {
      clearTimeout(pendingLessonDeleteRef.current.timer);
    }
    const timer = setTimeout(() => {
      pendingLessonDeleteRef.current = null;
      setPendingDeleteUi(null);
      deleteMutation.mutate(payload);
    }, 10_000);
    pendingLessonDeleteRef.current = { timer };
    setPendingDeleteUi({
      message: "Deleting lesson in 10 seconds",
      deadline: Date.now() + 10_000,
      durationMs: 10_000,
    });
    setLessonPendingDelete(null);
  }

  function cancelPendingEventDelete() {
    if (!pendingEventDeleteRef.current) return;
    clearTimeout(pendingEventDeleteRef.current.timer);
    pendingEventDeleteRef.current = null;
    setPendingDeleteUi(null);
  }

  function queueCustomEventDelete(id: string) {
    if (pendingEventDeleteRef.current) {
      clearTimeout(pendingEventDeleteRef.current.timer);
    }
    const timer = setTimeout(() => {
      pendingEventDeleteRef.current = null;
      setPendingDeleteUi(null);
      deleteCustomEventMutation.mutate(id);
    }, 10_000);
    pendingEventDeleteRef.current = { timer };
    setPendingDeleteUi({
      message: "Deleting event in 10 seconds",
      deadline: Date.now() + 10_000,
      durationMs: 10_000,
    });
  }

  function lessonFromCalendarEvent(event: CalendarEvent): Lesson | undefined {
    return lessons.find((l) => l.id === event.id);
  }

  function openStudentPage(event: CalendarEvent) {
    if (!event.studentId) return;
    navigate(`/students/${event.studentId}`);
  }

  function renderLessonActionsMenuContent(event: CalendarEvent) {
    if (event.source === "custom") {
      const hasStudent = Boolean(event.studentId && studentMap[event.studentId]);
      return (
        <>
          {event.studentId && (
            <DropdownMenuItem
              onClick={() => openStudentPage(event)}
              data-testid={`menu-open-student-${event.id}`}
            >
              <UserRound className="w-4 h-4 mr-2" />
              Open student
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => createFromCustomEventMutation.mutate(event)}
            disabled={!hasStudent || createFromCustomEventMutation.isPending}
            data-testid={`menu-create-lesson-custom-${event.id}`}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Create lesson
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => openVirtualEditDialog(event)}
            data-testid={`menu-reschedule-custom-${event.id}`}
          >
            <CalendarClock className="w-4 h-4 mr-2" />
            Reschedule
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => queueCustomEventDelete(event.id)}
            disabled={deleteCustomEventMutation.isPending}
            className="text-destructive focus:text-destructive"
            data-testid={`menu-delete-event-${event.id}`}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete event
          </DropdownMenuItem>
        </>
      );
    }
    const lesson = lessonFromCalendarEvent(event);
    if (!lesson) return null;
    return (
      <>
        {event.studentId && (
          <DropdownMenuItem onClick={() => openStudentPage(event)} data-testid={`menu-open-student-${event.id}`}>
            <UserRound className="w-4 h-4 mr-2" />
            Open student
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => openLessonRescheduleDialog(lesson)} data-testid={`menu-reschedule-lesson-${event.id}`}>
          <CalendarClock className="w-4 h-4 mr-2" />
          Reschedule
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openLessonDeleteConfirm(lesson)}
          disabled={deleteMutation.isPending}
          className="text-destructive focus:text-destructive"
          data-testid={`menu-delete-lesson-${event.id}`}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </>
    );
  }

  function handleSaveEdit() {
    if (editingVirtualEvent) {
      rescheduleMutation.mutate({
        event: editingVirtualEvent,
        date: editDate,
        time: editTime,
        duration: editDuration,
      });
      return;
    }
    if (!editingLesson) return;
    const [yy, mm, dd] = editDate.split("-").map(Number);
    const [th, tmin] = editTime.split(":").map(Number);
    const scheduledDate = localDateTimeToUTC(yy, mm, dd, th, tmin, teacherTz);
    updateMutation.mutate({
      id: editingLesson.id,
      data: { scheduledDate: scheduledDate.toISOString(), duration: editDuration },
    });
  }

  function closeEditDialog() {
    setEditingLesson(null);
    setEditingVirtualEvent(null);
  }

  function navigateToDayView(dayAnchor: Date) {
    setCurrentDate(dayAnchor);
    setSelectedDate(dayAnchor);
    setViewMode("day");
  }

  function handlePrev() {
    if (viewMode === "month") setCurrentDate(subMonths(currentDate, 1));
    else if (viewMode === "week") setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  }

  function handleNext() {
    if (viewMode === "month") setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === "week") setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  }

  function handleToday() {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  }

  function getHeaderLabel() {
    if (viewMode === "month") {
      const p = getLocalDateParts(currentDate, teacherTz);
      return new Date(p.year, p.month - 1, 15).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    }
    if (viewMode === "day") {
      const p = getLocalDateParts(currentDate, teacherTz);
      return new Intl.DateTimeFormat("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: teacherTz,
      }).format(localDateTimeToUTC(p.year, p.month, p.day, 12, 0, teacherTz));
    }
    const first = weekColumnInfos[0];
    const last = weekColumnInfos[6];
    const fmtShort = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: teacherTz });
    const fmtYear = new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: teacherTz,
    });
    return `${fmtShort.format(first.noon)} – ${fmtYear.format(last.noon)}`;
  }

  const dayViewKey = calendarDateKeyInTimezone(currentDate, teacherTz);
  const dayEvents = getEventsForWallDay(dayViewKey);
  const selectedDayEvents = selectedDate
    ? getEventsForWallDay(calendarDateKeyInTimezone(selectedDate, teacherTz))
    : [];

  if (lessonsLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  function renderEventCardCompact(event: CalendarEvent) {
    const chrome = getEventChromeClasses(event);
    const displayName = getEventDisplayName(event);
    const label = (
      <>
        {event.startTime} {displayName}
      </>
    );
    if (event.isVirtual) {
      return (
        <DropdownMenu key={event.id}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`text-[10px] sm:text-xs px-1 py-0.5 rounded truncate border w-full ${chrome}`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`calendar-event-${event.id}`}
            >
              {label}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={() => openStudentPage(event)} data-testid={`menu-open-student-${event.id}`}>
              <UserRound className="w-4 h-4 mr-2" />
              Open student
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => createFromScheduleMutation.mutate(event)}
              disabled={createFromScheduleMutation.isPending}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Create lesson
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openVirtualEditDialog(event)}>
              <CalendarClock className="w-4 h-4 mr-2" />
              Reschedule
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => cancelOccurrenceMutation.mutate(event)}
              disabled={cancelOccurrenceMutation.isPending}
              className="text-destructive focus:text-destructive"
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    return (
      <DropdownMenu key={event.id}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`text-[10px] sm:text-xs px-1 py-0.5 rounded truncate border w-full ${chrome}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`calendar-event-${event.id}`}
          >
            {label}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48" onClick={(e) => e.stopPropagation()}>
          {renderLessonActionsMenuContent(event)}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function renderEventCardFull(event: CalendarEvent) {
    const displayName = getEventDisplayName(event);
    const ongoing = !event.isVirtual && isOngoing(event);
    const typeLabel = event.lessonType === "offline" ? "offline" : "online";
    const typeBadge = event.lessonType === "offline"
      ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
      : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400";

    if (event.isVirtual) {
      const chrome = getEventChromeClasses(event);
      return (
        <div
          key={event.id}
          className={`flex items-center gap-3 p-3 rounded-lg border ${chrome}`}
          data-testid={`day-event-${event.id}`}
        >
          <div className="w-9 h-9 rounded-full bg-muted/30 flex items-center justify-center font-semibold text-xs flex-shrink-0">
            {displayName.slice(0, 2).toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate">
                {displayName}
              </p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeBadge}`}>{typeLabel}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {event.startTime} - {event.duration}min
              </span>
              <span className="flex items-center gap-1">
                <CalendarClock className="w-3 h-3" />
                Scheduled
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`button-edit-event-${event.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => openStudentPage(event)} data-testid={`menu-open-student-${event.id}`}>
                <UserRound className="w-4 h-4 mr-2" />
                Open student
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => createFromScheduleMutation.mutate(event)}
                disabled={createFromScheduleMutation.isPending}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Create lesson
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openVirtualEditDialog(event)}>
                <CalendarClock className="w-4 h-4 mr-2" />
                Reschedule
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => cancelOccurrenceMutation.mutate(event)}
                disabled={cancelOccurrenceMutation.isPending}
                className="text-destructive focus:text-destructive"
              >
                <Ban className="w-4 h-4 mr-2" />
                Cancel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }

    return (
      <div
        key={event.id}
        className={`flex items-center gap-3 p-3 rounded-lg border ${getEventChromeClasses(event)} ${ongoing ? "ring-2 ring-amber-400 dark:ring-amber-500" : ""}`}
        data-testid={`day-event-${event.id}`}
      >
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0 ${ongoing ? "bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-200" : "bg-primary/10 text-primary"}`}>
          {displayName.slice(0, 2).toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">
              {displayName}
            </p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeBadge}`}>{typeLabel}</span>
            {ongoing && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-200">NOW</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {event.startTime} - {event.duration}min
            </span>
            {event.address && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {event.address}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {event.source === "lesson" && (
            <Button
              variant={event.paymentStatus === "paid" ? "outline" : "default"}
              size="sm"
              onClick={() =>
                toggleStatusMutation.mutate({
                  lessonId: event.id,
                  status: event.paymentStatus === "paid" ? "pending" : "paid",
                })
              }
              data-testid={`button-cal-status-${event.id}`}
            >
              {event.paymentStatus === "paid" ? (
                <CheckCircle className="w-3.5 h-3.5" />
              ) : (
                <AlertCircle className="w-3.5 h-3.5" />
              )}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`button-edit-event-${event.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {renderLessonActionsMenuContent(event)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  function renderTimelineBlock(event: CalendarEvent, layout?: LayoutInfo) {
    const displayName = getEventDisplayName(event);
    const top = getMinuteOffsetFromScheduled(event.scheduledDate);
    const height = getLessonHeightPx(event.duration);

    const col = layout?.col ?? 0;
    const totalCols = layout?.totalCols ?? 1;
    const widthPct = 100 / totalCols;
    const leftPct = col * widthPct;

    const blockClass = getEventChromeClasses(event);
    const ongoing = !event.isVirtual && isOngoing(event);

    const inner = (
      <>
        <div className="pointer-events-none text-[10px] sm:text-xs font-medium truncate leading-tight flex items-center gap-0.5">
          <MoreHorizontal className="w-3 h-3 shrink-0 opacity-70" />
          {event.startTime} {displayName}
        </div>
        {height > pxPerHour * 0.45 && (
          <div className="pointer-events-none text-[9px] sm:text-[10px] opacity-70 truncate leading-tight">
            {event.duration}min · {event.lessonType}{ongoing ? " · NOW" : ""}
          </div>
        )}
        {height > pxPerHour * 0.85 && event.address && (
          <div className="pointer-events-none text-[9px] opacity-60 truncate leading-tight">
            {event.address}
          </div>
        )}
      </>
    );

    if (event.isVirtual) {
      return (
        <DropdownMenu key={event.id}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`absolute rounded px-1.5 py-0.5 overflow-hidden border transition-shadow hover:shadow-md z-10 text-left w-full ${blockClass}`}
              style={{
                top: `${top}px`,
                height: `${height}px`,
                minHeight: "24px",
                left: `calc(${leftPct}% + 2px)`,
                width: `calc(${widthPct}% - 4px)`,
              }}
              data-testid={`timeline-event-${event.id}`}
            >
              {inner}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={() => openStudentPage(event)} data-testid={`menu-open-student-${event.id}`}>
              <UserRound className="w-4 h-4 mr-2" />
              Open student
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => createFromScheduleMutation.mutate(event)}
              disabled={createFromScheduleMutation.isPending}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Create lesson
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openVirtualEditDialog(event)}>
              <CalendarClock className="w-4 h-4 mr-2" />
              Reschedule
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => cancelOccurrenceMutation.mutate(event)}
              disabled={cancelOccurrenceMutation.isPending}
              className="text-destructive focus:text-destructive"
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <DropdownMenu key={event.id}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`absolute rounded px-1.5 py-0.5 overflow-hidden border transition-shadow hover:shadow-md z-10 text-left w-full ${blockClass} ${ongoing ? "ring-2 ring-amber-400 dark:ring-amber-500" : ""}`}
            style={{
              top: `${top}px`,
              height: `${height}px`,
              minHeight: "24px",
              left: `calc(${leftPct}% + 2px)`,
              width: `calc(${widthPct}% - 4px)`,
            }}
            data-testid={`timeline-event-${event.id}`}
          >
            {inner}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {renderLessonActionsMenuContent(event)}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  function renderTimeGutter() {
    return (
      <div className="flex-shrink-0 w-12 sm:w-14 relative" style={{ height: `${24 * pxPerHour + 30}px` }}>
        {HOURS.map(h => (
          <div
            key={h}
            className="absolute left-0 right-0 text-[10px] sm:text-xs text-muted-foreground pr-1 text-right leading-none"
            style={{ top: `${h * pxPerHour - 6}px` }}
          >
            {String(h).padStart(2, "0")}:00
          </div>
        ))}
      </div>
    );
  }

  function renderHourLines() {
    return HOURS.map(h => (
      <div
        key={h}
        className="absolute left-0 right-0 border-t border-border/40"
        style={{ top: `${h * pxPerHour}px` }}
      />
    ));
  }

  function renderNowLine(columnDateKey: string) {
    if (columnDateKey !== todayKeyInTeacherTz) return null;
    const now = new Date();
    const { hour, minute } = getLocalDateParts(now, teacherTz);
    const nowTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    const top = getMinuteOffsetFromTime(nowTime);
    return (
      <div
        className="absolute left-0 right-0 z-20 pointer-events-none"
        style={{ top: `${top}px` }}
      >
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
          <div className="flex-1 h-px bg-red-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 max-w-7xl mx-auto" data-testid="page-calendar">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="text-page-title">Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">View and manage your lesson schedule</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-0.5 border rounded-md">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-none"
              onClick={() => setPxPerHour((p) => Math.max(PX_MIN, p - 8))}
              title="Zoom out (less detail per hour)"
              data-testid="button-calendar-zoom-out"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-none border-x"
              onClick={() => setPxPerHour((p) => Math.min(PX_MAX, p + 8))}
              title="Zoom in (more detail per hour)"
              data-testid="button-calendar-zoom-in"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncFromGoogleMutation.mutate()}
            disabled={syncFromGoogleMutation.isPending}
            data-testid="button-sync-gcal"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncFromGoogleMutation.isPending ? 'animate-spin' : ''}`} />
            {syncFromGoogleMutation.isPending ? "Syncing..." : "Sync"}
          </Button>
          <ScheduleEventDialog
            open={showScheduleEvent}
            onOpenChange={setShowScheduleEvent}
            students={students}
            requireStudentSelection
            trigger={
              <Button size="sm" data-testid="button-calendar-schedule-event">
                <Plus className="w-4 h-4 mr-1.5" />
                Schedule new event
              </Button>
            }
          />
          <div className="flex items-center gap-1" data-testid="view-mode-toggle">
            {(["month", "week", "day"] as ViewMode[]).map(mode => (
              <Button
                key={mode}
                variant={viewMode === mode ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode(mode)}
                data-testid={`button-view-${mode}`}
                className="toggle-elevate"
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <Card
        className="p-5"
        onWheelCapture={handleTimelineWheel}
        onTouchStart={handleTouchPinchStart}
        onTouchMove={handleTouchPinchMove}
        onTouchEnd={handleTouchPinchEnd}
        onTouchCancel={handleTouchPinchEnd}
      >
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handlePrev} data-testid="button-prev-period">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleNext} data-testid="button-next-period">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleToday} data-testid="button-today">
              Today
            </Button>
          </div>
          <h2 className="text-lg font-semibold" data-testid="text-current-period">
            {getHeaderLabel()}
          </h2>
          <div className="flex items-center gap-3 text-[10px] sm:text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border-2 border-dashed border-blue-400/55 bg-blue-500/12 inline-block" />
              Online (upcoming)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border-2 border-dashed border-amber-500/55 bg-amber-500/12 inline-block" />
              Offline (upcoming)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border-2 border-blue-600 bg-blue-200/50 dark:bg-blue-950/60 inline-block" />
              Past
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded border-2 border-amber-500 bg-amber-100/80 dark:bg-amber-950/50 inline-block" />
              Now
            </span>
          </div>
        </div>

        {viewMode === "month" && (
          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
            {[
              { short: "M", full: "Mon" },
              { short: "T", full: "Tue" },
              { short: "W", full: "Wed" },
              { short: "T", full: "Thu" },
              { short: "F", full: "Fri" },
              { short: "S", full: "Sat" },
              { short: "S", full: "Sun" },
            ].map((d, i) => (
              <div key={i} className="bg-muted/50 p-1.5 sm:p-2 text-center text-[10px] sm:text-xs font-medium text-muted-foreground">
                <span className="sm:hidden">{d.short}</span>
                <span className="hidden sm:inline">{d.full}</span>
              </div>
            ))}
            {monthGridCells.map((cell, i) => {
              const dEvents = getEventsForWallDay(cell.key);
              const isSelected =
                !!selectedDate &&
                calendarDateKeyInTimezone(selectedDate, teacherTz) === cell.key;
              const isTodayCell = cell.key === todayKeyInTeacherTz;
              return (
                <div
                  key={i}
                  className={`min-h-[80px] sm:min-h-[100px] p-1.5 bg-background cursor-pointer transition-colors ${
                    !cell.inMonth ? "opacity-40" : ""
                  } ${isSelected ? "ring-2 ring-primary ring-inset" : ""} ${
                    isTodayCell ? "bg-primary/5" : ""
                  }`}
                  data-calendar-day={cell.key}
                  onClick={() => setSelectedDate(cell.noon)}
                  onDoubleClick={() => navigateToDayView(cell.noon)}
                  data-testid={`calendar-day-${cell.key}`}
                >
                  <span className={`text-xs font-medium block mb-1 ${
                    isTodayCell ? "text-primary font-bold" : ""
                  }`}>
                    {cell.wall.day}
                  </span>
                  <div className="space-y-0.5">
                    {dEvents.slice(0, 3).map(event => renderEventCardCompact(event))}
                    {dEvents.length > 3 && (
                      <span className="text-[10px] text-muted-foreground pl-1">
                        +{dEvents.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {viewMode === "week" && (
          <div
            ref={timelineRef}
            className="overflow-y-auto overflow-x-auto"
            style={{ maxHeight: "calc(100vh - 250px)" }}
            data-testid="week-view-content"
            onWheel={handleTimelineWheel}
          >
            <div className="flex min-w-[600px]">
              <div className="flex-shrink-0 w-12 sm:w-14">
                <div className="h-12 sm:h-14" />
                <div className="relative" style={{ height: `${24 * pxPerHour + 30}px` }}>
                  {HOURS.map(h => (
                    <div
                      key={h}
                      className="absolute left-0 right-0 text-[10px] sm:text-xs text-muted-foreground pr-1 text-right leading-none"
                      style={{ top: `${h * pxPerHour - 6}px` }}
                    >
                      {String(h).padStart(2, "0")}:00
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 grid grid-cols-7 gap-px bg-border/50">
                {weekColumnInfos.map((col, i) => {
                  const dEvents = getEventsForWallDay(col.key);
                  const today = col.key === todayKeyInTeacherTz;
                  const weekdayShort = new Intl.DateTimeFormat("en-GB", {
                    weekday: "short",
                    timeZone: teacherTz,
                  }).format(col.noon);
                  return (
                    <div key={i} className="min-w-0 flex flex-col" data-calendar-day={col.key}>
                      <div
                        className={`h-12 sm:h-14 flex flex-col items-center justify-center sticky top-0 z-30 bg-background border-b cursor-pointer ${
                          today ? "bg-primary/5" : ""
                        }`}
                        onClick={() => navigateToDayView(col.noon)}
                        data-testid={`week-header-${col.key}`}
                      >
                        <span className="text-[10px] sm:text-xs text-muted-foreground">
                          {weekdayShort}
                        </span>
                        <span className={`text-sm sm:text-base font-semibold ${
                          today ? "bg-primary text-primary-foreground w-7 h-7 rounded-full flex items-center justify-center" : ""
                        }`}>
                          {col.wall.day}
                        </span>
                      </div>

                      <div
                        className={`relative ${today ? "bg-primary/[0.02]" : "bg-background"}`}
                        data-calendar-timeline=""
                        style={{ height: `${24 * pxPerHour + 30}px` }}
                      >
                        {renderHourLines()}
                        {renderNowLine(col.key)}
                        {(() => {
                          const layoutMap = computeOverlapLayout(dEvents, teacherTz);
                          return dEvents.map(event => renderTimelineBlock(event, layoutMap.get(event.id)));
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {viewMode === "day" && (
          <div
            ref={timelineRef}
            className="overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 250px)" }}
            data-testid="day-view-content"
            onWheel={handleTimelineWheel}
          >
            <div className="flex">
              {renderTimeGutter()}

              <div
                className="flex-1 relative bg-background"
                data-calendar-day={dayViewKey}
                data-calendar-timeline=""
                style={{ height: `${24 * pxPerHour + 30}px` }}
              >
                {renderHourLines()}
                {renderNowLine(dayViewKey)}
                {(() => {
                  const layoutMap = computeOverlapLayout(dayEvents, teacherTz);
                  return dayEvents.map(event => renderTimelineBlock(event, layoutMap.get(event.id)));
                })()}
              </div>
            </div>
          </div>
        )}
      </Card>

      {viewMode === "month" && selectedDate && (
        <Card
          className="p-5"
          data-testid="card-selected-day"
          data-calendar-day={calendarDateKeyInTimezone(selectedDate, teacherTz)}
        >
          <h3 className="font-semibold mb-3">
            {new Intl.DateTimeFormat("en-GB", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              timeZone: teacherTz,
            }).format(selectedDate)}
          </h3>
          {selectedDayEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events scheduled</p>
          ) : (
            <div className="space-y-3">
              {selectedDayEvents.map(event => renderEventCardFull(event))}
            </div>
          )}
        </Card>
      )}

      <Dialog open={!!editingLesson || !!editingVirtualEvent} onOpenChange={(open) => { if (!open) closeEditDialog(); }}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Reschedule lesson</DialogTitle>
          </DialogHeader>
          {(editingLesson || editingVirtualEvent) && (() => {
            const studentId = editingLesson?.studentId || editingVirtualEvent?.studentId || "";
            const lessonType = editingLesson?.lessonType || editingVirtualEvent?.lessonType || "";
            const unitRate = editingLesson?.unitRate || editingVirtualEvent?.unitRate || "";
            const unitCurrency = editingLesson?.unitCurrency || editingVirtualEvent?.unitCurrency || "";
            return (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">
                    {studentMap[studentId]?.name || "Unknown"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {lessonType} · {unitRate} {unitCurrency}
                  </p>
                </div>
                {editingVirtualEvent ? (
                  <p className="text-xs text-muted-foreground">
                    Change the date, time, or duration for this single occurrence. This won't affect the recurring schedule.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Change the time for this lesson only. The recurring student schedule is not modified.
                  </p>
                )}
                <div className="space-y-2">
                  <Label>Date</Label>
                  <DateInput value={editDate} onChange={setEditDate} data-testid="input-edit-date" />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <ScheduleTimePicker
                    value={editTime}
                    onChange={setEditTime}
                    aria-label="Lesson time"
                    data-testid="input-edit-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (min)</Label>
                  <Input type="number" min="5" max="300" step="5" value={editDuration} onChange={(e) => setEditDuration(parseInt(e.target.value) || 60)} data-testid="input-edit-duration" />
                </div>
                <DialogFooter className="flex flex-row flex-wrap justify-end gap-2 sm:gap-2">
                  <Button type="button" variant="outline" onClick={closeEditDialog}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending || rescheduleMutation.isPending}
                    data-testid="button-save-edit-lesson"
                  >
                    {(updateMutation.isPending || rescheduleMutation.isPending)
                      ? "Saving..."
                      : editingVirtualEvent
                        ? "Reschedule"
                        : "Save"}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={lessonPendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setLessonPendingDelete(null);
        }}
      >
        <AlertDialogContent className="max-w-md" aria-describedby="delete-lesson-desc">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lesson?</AlertDialogTitle>
            <AlertDialogDescription id="delete-lesson-desc">
              This removes the lesson from your schedule. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 py-1">
            <Checkbox
              id="delete-lesson-remove-gcal"
              checked={removeCalOnDelete}
              onCheckedChange={(c) => setRemoveCalOnDelete(c === true)}
            />
            <label htmlFor="delete-lesson-remove-gcal" className="text-sm text-muted-foreground leading-snug cursor-pointer">
              Remove scheduled event from Google Calendar as well
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-lesson"
              onClick={() => {
                if (!lessonPendingDelete) return;
                queueLessonDelete({
                  id: lessonPendingDelete.id,
                  removeCalendarEvent: removeCalOnDelete,
                });
              }}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UndoDeleteBanner
        open={!!pendingDeleteUi}
        message={pendingDeleteUi?.message || ""}
        deadline={pendingDeleteUi?.deadline || 0}
        durationMs={pendingDeleteUi?.durationMs || 10_000}
        onUndo={() => {
          if (pendingLessonDeleteRef.current) cancelPendingLessonDelete();
          else cancelPendingEventDelete();
        }}
      />

    </div>
  );
}
